/**
 * Cookie Consent Entrypoint
 *
 * Initializes vanilla-cookieconsent with categories for analytics and marketing.
 * Scripts are gated based on user consent choices.
 */
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import {
  consentStore,
  CONSENT_CATEGORIES,
  type ConsentCategory,
} from "@/stores/consent-store";
import { loadConsentedScripts } from "@/utils/consent-scripts";

const VALID_CATEGORIES = Object.values(CONSENT_CATEGORIES);

const toConsentCategories = (categories: string[]): ConsentCategory[] =>
  categories.filter((c): c is ConsentCategory =>
    VALID_CATEGORIES.includes(c as ConsentCategory)
  );

const syncToShopify = (categories: ConsentCategory[]): void => {
  const customerPrivacy = window.Shopify?.customerPrivacy;
  if (!customerPrivacy) {
    console.log("[Consent] Shopify Customer Privacy API not available");
    return;
  }

  const analytics = categories.includes(CONSENT_CATEGORIES.ANALYTICS);
  const marketing = categories.includes(CONSENT_CATEGORIES.MARKETING);

  customerPrivacy.setTrackingConsent(
    {
      analytics,
      marketing,
      preferences: true,
    },
    () => console.log("[Consent] Synced to Shopify Customer Privacy API")
  );
};

const syncConsentState = (categories: ConsentCategory[]): void => {
  consentStore.getState().syncFromCookie(categories);
  syncToShopify(categories);
  loadConsentedScripts();
};

const initCookieConsent = (): void => {
  CookieConsent.run({
    guiOptions: {
      consentModal: {
        layout: "box inline",
        position: "bottom left",
      },
      preferencesModal: {
        layout: "box",
      },
    },

    categories: {
      [CONSENT_CATEGORIES.NECESSARY]: {
        enabled: true,
        readOnly: true,
      },
      [CONSENT_CATEGORIES.ANALYTICS]: {
        enabled: false,
        readOnly: false,
        autoClear: {
          cookies: [{ name: /^_ga/ }, { name: "_gid" }],
        },
      },
      [CONSENT_CATEGORIES.MARKETING]: {
        enabled: false,
        readOnly: false,
      },
    },

    language: {
      default: "en",
      translations: {
        en: {
          consentModal: {
            title: "We use cookies",
            description:
              "We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking \"Accept All\", you consent to our use of cookies.",
            acceptAllBtn: "Accept All",
            acceptNecessaryBtn: "Reject All",
            showPreferencesBtn: "Manage Preferences",
          },
          preferencesModal: {
            title: "Cookie Preferences",
            acceptAllBtn: "Accept All",
            acceptNecessaryBtn: "Reject All",
            savePreferencesBtn: "Save Preferences",
            closeIconLabel: "Close",
            sections: [
              {
                title: "Cookie Usage",
                description:
                  "We use cookies to ensure basic site functionality and to enhance your online experience. You can choose to opt in or out of each category whenever you want.",
              },
              {
                title: "Strictly Necessary Cookies",
                description:
                  "These cookies are essential for the website to function properly. They enable basic features like page navigation, access to secure areas, and shopping cart functionality.",
                linkedCategory: CONSENT_CATEGORIES.NECESSARY,
              },
              {
                title: "Analytics Cookies",
                description:
                  "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our site.",
                linkedCategory: CONSENT_CATEGORIES.ANALYTICS,
              },
              {
                title: "Marketing Cookies",
                description:
                  "These cookies are used to track visitors across websites to display relevant advertisements. They help us measure the effectiveness of our marketing campaigns.",
                linkedCategory: CONSENT_CATEGORIES.MARKETING,
              },
            ],
          },
        },
      },
    },

    onConsent: ({ cookie }) => {
      console.log("[Consent] User consent saved:", cookie.categories);
      syncConsentState(toConsentCategories(cookie.categories));
    },

    onChange: ({ cookie }) => {
      console.log("[Consent] Preferences changed:", cookie.categories);
      syncConsentState(toConsentCategories(cookie.categories));
    },
  });

  // Sync initial state if user already consented
  if (CookieConsent.acceptedCategory(CONSENT_CATEGORIES.NECESSARY)) {
    const categories: ConsentCategory[] = [];
    if (CookieConsent.acceptedCategory(CONSENT_CATEGORIES.ANALYTICS)) {
      categories.push(CONSENT_CATEGORIES.ANALYTICS);
    }
    if (CookieConsent.acceptedCategory(CONSENT_CATEGORIES.MARKETING)) {
      categories.push(CONSENT_CATEGORIES.MARKETING);
    }
    syncConsentState(categories);
  }

  console.log("[Consent] Cookie consent initialized");
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCookieConsent);
} else {
  initCookieConsent();
}
